import flatten from 'lodash/flatten';

import {PropertyType, ResourceType} from 'common/report/constants';
import {SelectorType, SelectorTypeDefinitions} from 'common/query-builder/query-entry-section/types';
import {Segmentation} from 'common/types/reports/query';
import {getBucketType} from 'common/widgets/bucket-pane/numeric-bucket-pane/util';

import {Action, EntryType} from './types';

export type ResourceTrackingTypes = SelectorType | EntryType;

const BaseTracking = {
  [Action.AddFilter]: `[mp-query-builder] Add event filter`,
  [Action.EditFilter]: `[mp-query-builder] Edit event filter`,
  [Action.RemoveFilter]: `[mp-query-builder] Remove event filter`,
};

export const ResourceTrackingEvent: {[s in ResourceTrackingTypes]?: {[key in Action]?: string}} = {
  [ResourceType.Cohort]: {
    ...BaseTracking,
    [Action.Add]: `[mp-query-builder] Add cohort`,
    [Action.Clone]: `[mp-query-builder] Clone cohort`,
    [Action.Drag]: `[mp-query-builder] Drag a cohort`,
    [Action.Remove]: `[mp-query-builder] Remove cohort`,
    [Action.Select]: `[mp-query-builder] Select cohort`,
  },
  [ResourceType.Event]: {
    ...BaseTracking,
    [Action.Add]: `[mp-query-builder] Add event`,
    [Action.Clone]: `[mp-query-builder] Clone event`,
    [Action.Drag]: `[mp-query-builder] Drag an event`,
    [Action.Remove]: `[mp-query-builder] Remove event`,
    [Action.Select]: `[mp-query-builder] Select event`,
    [Action.Group]: `[mp-query-builder] Add event comparison`,
    [Action.Ungroup]: `[mp-query-builder] Remove event comparison`,
  },
  [ResourceType.Formula]: {
    [Action.Add]: `[mp-query-builder] Add formula`,
    [Action.Edit]: `[mp-query-builder] Edit formula`,
    [Action.Clone]: `[mp-query-builder] Clone formula`,
    [Action.Drag]: `[mp-query-builder] Drag formula`,
    [Action.Remove]: `[mp-query-builder] Remove formula`,
  },
  [SelectorTypeDefinitions.GroupedEvent]: {
    ...BaseTracking,
    [Action.Drag]: `[mp-query-builder] Drag a grouped event`,
  },
  [SelectorTypeDefinitions.FlowsEvent]: {
    ...BaseTracking,
    [Action.Add]: `[mp-query-builder] Add event`,
    [Action.Clone]: `[mp-query-builder] Clone event`,
    [Action.Drag]: `[mp-query-builder] Drag an event`,
    [Action.Remove]: `[mp-query-builder] Remove event`,
    [Action.Select]: `[mp-query-builder] Select event`,
    [Action.EditFlowSteps]: `[mp-query-builder] Edit Flow Steps`,
  },
  [SelectorTypeDefinitions.PropertyExpansion]: {
    [Action.Add]: `[mp-query-builder] Add property expansion`,
    [Action.Clone]: `[mp-query-builder] Clone property expansion`,
    [Action.Drag]: `[mp-query-builder] Drag property expansion`,
    [Action.Remove]: `[mp-query-builder] Remove property expansion`,
    [Action.Select]: `[mp-query-builder] Select property expansion`,
  },
  [ResourceType.User]: {
    [Action.Add]: `[mp-query-builder] Add user group`,
    [Action.Clone]: `[mp-query-builder] Clone user group`,
    [Action.Drag]: `[mp-query-builder] Drag user group`,
    [Action.Remove]: `[mp-query-builder] Remove user group`,
    [Action.Select]: `[mp-query-builder] Select user group`,
  },
  [SelectorTypeDefinitions.CohortGroup]: {
    [Action.Add]: `[mp-query-builder] Add user group`,
    [Action.Clone]: `[mp-query-builder] Clone user group`,
    [Action.Drag]: `[mp-query-builder] Drag user group`,
    [Action.Remove]: `[mp-query-builder] Remove user group`,
    [Action.Select]: `[mp-query-builder] Select user group`,
  },
  [SelectorTypeDefinitions.Filter]: {
    [Action.Add]: `[mp-query-builder] Add property filter`,
    [Action.Clone]: `[mp-query-builder] Clone property filter`,
    [Action.Drag]: `[mp-query-builder] Drag a property filter`,
    [Action.Edit]: `[mp-query-builder] Edit property filter`,
    [Action.Remove]: `[mp-query-builder] Remove property filter`,
  },
  [SelectorTypeDefinitions.Segment]: {
    [Action.Add]: `[mp-query-builder] Add segment`,
    [Action.Clone]: `[mp-query-builder] Clone segment`,
    [Action.Drag]: `[mp-query-builder] Reorder segment`,
    [Action.Edit]: `[mp-query-builder] Edit segment`,
    [Action.Remove]: `[mp-query-builder] Remove segment`,
    [Action.ToggleValueComparison]: `[mp-query-builder] Toggle segment value comparison`,
  },
  [SelectorTypeDefinitions.Behavior]: {
    [Action.Add]: `[mp-query-builder] Add behavior`,
    [Action.Clone]: `[mp-query-builder] Clone behavior`,
    [Action.Drag]: `[mp-query-builder] Drag a behavior`,
    [Action.Remove]: `[mp-query-builder] Remove behavior`,
    [Action.Select]: `[mp-query-builder] Select behavior`,
    [Action.Edit]: `[mp-query-builder] Edit behavior`,
    [Action.EditFilter]: `[mp-query-builder] Edit behavior filter`,
    [Action.SelectDateRange]: `[mp-query-builder] Select behavior date range`,
  },
  [SelectorTypeDefinitions.EventLaunch]: {
    [Action.Add]: `[mp-query-builder] Add event`,
    [Action.Clone]: `[mp-query-builder] Clone event`,
    [Action.Drag]: `[mp-query-builder] Drag an event`,
    [Action.Remove]: `[mp-query-builder] Remove event`,
    [Action.Select]: `[mp-query-builder] Select event`,
    [Action.SelectDateRange]: `[mp-query-builder] Select date range`,
  },
  [EntryType.ExclusionEvent]: {
    [Action.Add]: `[mp-query-builder] Add exclusion step`,
    [Action.AddFilter]: `[mp-query-builder] Add exclusion step filter`,
    [Action.Clone]: `[mp-query-builder] Clone exclusion step`,
    [Action.EditFilter]: `[mp-query-builder] Edit exclusion step filter`,
    [Action.Remove]: `[mp-query-builder] Remove exclusion step`,
    [Action.RemoveFilter]: `[mp-query-builder] Remove exclusion step filter`,
    [Action.Select]: `[mp-query-builder] Select exclusion step`,
  },
  'metric-entry': {
    ...BaseTracking,
  },
};

export const FlattenedResourceTrackingEvent = flatten(
  Object.values(ResourceTrackingEvent).map((entry) => Object.values(entry)),
);

export enum TrackingEvent {
  ToggleExpandRow = `[mp-query-builder] Toggle Expand Row`,
  ResetFormula = `[mp-query-builder] Reset Formula Changes`,
  SaveFormula = `[mp-query-builder] Save Formula`,
  SelectFormula = `[mp-query-builder] Select Formula`,
  SaveFormulaChanges = `[mp-query-builder] Save Formula Changes`,
  AddEntryRow = `[mp-query-builder] Add Entry Row`,
  ToggleVisibility = `[mp-query-builder] Toggle Entry Row Visibility`,
  EditExclusionStepRange = `[mp-query-builder] Edit exclusion step range`,
  EventIndexLabelClicked = `[mp-query-builder] Event index label clicked`,
  RenameEvent = `[mp-query-builder] Rename event`,
  RenameFormula = `[mp-query-builder] Rename formula`,
  RenameFunnelMetric = `[mp-query-builder] Rename Funnel Metric`,
  RenameRetentionMetric = `[mp-query-builder] Rename Retention Metric`,
  RenameRetentionFrequencyMetric = `[mp-query-builder] Rename Retention-Frequency Metric`,
  ClearNumericProperty = `[mp-query-builder] Clear numeric property`,
  SelectNumericAggregator = `[mp-query-builder] Select numeric aggregator`,
  SelectNumericProperty = `[mp-query-builder] Select numeric property`,
  OpenCustomBucketPane = `[mp-query-builder] Open custom bucket pane`,
  ResetCustomBucket = `[mp-query-builder] Reset custom bucket`,
  ApplyCustomBucket = `[mp-query-builder] Apply custom bucket`,
  ToggleGroupingOperator = `[mp-query-builder] Toggled grouping operator`,
  MapTableClicked = `[Lookup table] Map to table clicked`,
  UnmappedTable = `[Lookup table] Unmapped`,
}

export function getCustomizeGroupsTrackingProps(segment: Segmentation) {
  const baseProps = {
    'Property Type': segment.propertyType,
    'Property Name': segment.propertyName,
    'Default Property Type': segment.propertyDefaultType,
  };

  if (segment?.[`customGroups`] && [PropertyType.String, PropertyType.List].includes(segment.propertyType)) {
    return {
      ...baseProps,
      'Number of Segments': segment[`customGroups`].length,
      'Custom Groups': segment[`customGroups`].map((group) => {
        return {
          filterOperator: group.filterOperator,
          filterValues: group.filterValue,
          'Number of filter values': Array.isArray(group.filterValue) ? group.filterValue.length : 1,
        };
      }),
    };
  } else if (segment?.[`customBucket`] && segment.propertyType === PropertyType.Number) {
    return {
      ...baseProps,
      'Number of Variable Buckets': segment?.[`customBucket`]?.[`groups`]
        ? segment[`customBucket`][`groups`].length + 1
        : null,
      'Bucketing Type': getBucketType(segment[`customBucket`]),
    };
  } else {
    return baseProps;
  }
}
